import * as React from 'react';
import Container from '@mui/material/Container';
import Paper from '@mui/material/Paper';
import Panel from '../panel/Panel';
import { observer } from 'mobx-react-lite';
import { useStores } from '../../stores';
import { useTranslation } from 'react-i18next';
import { Button, IconButton, LinearProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import CartWitnessingPlaceDialog from './dialogs/CartWitnessingPlaceDialog';
import { useOpenDialog } from '../../hooks/useOpenDialog';
import { CartWitnessingPlace } from '../../domain/CartWitnessingPlace';
import { useConfirm } from 'material-ui-confirm';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import Can from '../../components/Can';

const CartWitnessingPlacesPage = observer(() => {
    const { t } = useTranslation();
    const { cartWitnessingStore, serverStore } = useStores();
    const { isOpen, openDialog, closeDialog } = useOpenDialog();
    const confirm = useConfirm();
    const [selectedPlace, setSelectedPlace] = React.useState<CartWitnessingPlace | undefined>();

    React.useEffect(() => {
        cartWitnessingStore.getAll();
    }, [cartWitnessingStore]);

    const handleOpenCreate = () => {
        setSelectedPlace(undefined);
        openDialog('create-cart-place');
    };

    const handleOpenEdit = (place: CartWitnessingPlace) => {
        setSelectedPlace(place);
        openDialog('edit-cart-place');
    };

    const handleCloseDialog = () => {
        setSelectedPlace(undefined);
        closeDialog();
    };

    const handleCreate = (place: CartWitnessingPlace) => {
        cartWitnessingStore.createPlace(place, handleCloseDialog);
    };

    const handleUpdate = (place: CartWitnessingPlace) => {
        if (!selectedPlace) {
            return;
        }
        cartWitnessingStore.updatePlace(selectedPlace.id, place, handleCloseDialog);
    };

    const handleDelete = (place: CartWitnessingPlace) => {
        confirm({
            title: t('are_you_sure'),
            description: `${t('this_will_permanently_delete')} ${place.name}`,
            confirmationText: t('yes'),
            cancellationText: t('no')
        }).then(() => {
            cartWitnessingStore.deletePlace(place.id);
        });
    };

    const headerItems = [
        <Can key="create_cart_place_btn" do="create" on='CartWitnessingPlace'>
            <Button type="button" color="inherit" onClick={handleOpenCreate}>
                {t('create_new_place')}
            </Button>
        </Can>
    ];

    return (
        <Panel title={t('places')} headerItems={headerItems}>
            <Container maxWidth="md" sx={{ mt: 4, mb: 4 }}>
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {serverStore.serverRequestLoading && <LinearProgress />}
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>#</TableCell>
                                    <TableCell>{t('name')}</TableCell>
                                    <TableCell>{t('address')}</TableCell>
                                    <TableCell>{t('actions')}</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {cartWitnessingStore.places.map((place, index) => (
                                    <TableRow key={place.id}>
                                        <TableCell>{index + 1}</TableCell>
                                        <TableCell>{place.name}</TableCell>
                                        <TableCell>{place.address || '-'}</TableCell>
                                        <TableCell>
                                            <Can do="update" on='CartWitnessingPlace'>
                                                <IconButton onClick={() => handleOpenEdit(place)} size="small">
                                                    <EditIcon color="primary" />
                                                </IconButton>
                                            </Can>
                                            <Can do="delete" on='CartWitnessingPlace'>
                                                <IconButton onClick={() => handleDelete(place)} size="small">
                                                    <DeleteIcon color="error" />
                                                </IconButton>
                                            </Can>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {cartWitnessingStore.places.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={4}>{t('empty')}</TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Paper>
            </Container>
            <CartWitnessingPlaceDialog
                isOpen={isOpen('create-cart-place')}
                onSubmit={handleCreate}
                onClose={handleCloseDialog}
            />
            <CartWitnessingPlaceDialog
                isOpen={isOpen('edit-cart-place')}
                cartPlace={selectedPlace}
                onSubmit={handleUpdate}
                onClose={handleCloseDialog}
            />
        </Panel>
    );
});

export default CartWitnessingPlacesPage;
